import React from 'react';
import { Button } from '../components/ui/Button';
import { Card, CardBody } from '../components/ui/Card';
import { CtaSection } from '../components/sections/CtaSection';
import { CheckIcon, UsersIcon, LightbulbIcon, GlobeIcon, BookOpenIcon, AwardIcon, BriefcaseIcon } from 'lucide-react';
export function Solutions() {
  const solutions = [{
    title: 'Meetings & Retreats',
    description: 'Strategic gatherings that spark innovation and alignment while providing a refreshing change of environment.',
    outcomes: ['Increased team alignment', 'Accelerated decision making', 'Enhanced collaboration', 'Improved employee engagement'],
    activities: ['Strategy sprints', 'Executive cabanas', 'Beachside breakouts', 'Sunrise wellness sessions'],
    image: 'https://images.unsplash.com/photo-1517048676732-d65bc937f952?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80'
  }, {
    title: 'Incentives',
    description: 'Reward top performers with unforgettable experiences that inspire future success and drive motivation.',
    outcomes: ['Boosted team morale', 'Increased retention', 'Motivation for future performance', 'Strengthened company culture'],
    activities: ['Luxury excursions', 'VIP experiences', 'Gala dinners', 'Adventure activities'],
    image: 'https://images.unsplash.com/photo-1544551763-46a013bb70d5?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80'
  }, {
    title: 'Brand Launches',
    description: 'Create buzz and alignment around your new product or initiative with immersive brand experiences.',
    outcomes: ['Consistent messaging', 'Enthusiastic brand advocates', 'Memorable product experiences', 'Media-worthy moments'],
    activities: ['Immersive product demos', 'Media moments', 'Training workshops', 'Brand activation experiences'],
    image: 'https://images.unsplash.com/photo-1475721027785-f74eccf877e2?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80'
  }, {
    title: 'Hybrid/Virtual Enhancements',
    description: 'Extend your in-person event to remote participants with engaging digital experiences.',
    outcomes: ['Inclusive participation', 'Extended reach', 'Content longevity', 'Cost-effective engagement'],
    activities: ['Livestreams', 'Virtual chat', 'Interactive polling', 'Digital breakout rooms'],
    image: 'https://images.unsplash.com/photo-1609921212029-bb5a28e60960?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80'
  }];
  // New expert trainers data
  const expertCategories = [{
    title: 'Leadership Development',
    icon: <AwardIcon className="h-8 w-8 text-[#0099A1]" data-id="element-1559" />,
    description: 'Transform managers into inspiring leaders through immersive workshops and personalized coaching.',
    experts: ['Harvard Business School', 'Wharton', 'Stanford GSB']
  }, {
    title: 'Innovation & Design Thinking',
    icon: <LightbulbIcon className="h-8 w-8 text-[#0099A1]" data-id="element-1560" />,
    description: 'Unlock creative problem-solving with hands-on workshops led by innovation experts.',
    experts: ['IDEO Alumni', 'MIT Media Lab', 'Stanford d.school']
  }, {
    title: 'Strategic Planning',
    icon: <BriefcaseIcon className="h-8 w-8 text-[#0099A1]" data-id="element-1561" />,
    description: 'Develop actionable roadmaps with guidance from experienced strategy consultants and academics.',
    experts: ['McKinsey Alumni', 'London Business School', 'INSEAD']
  }, {
    title: 'Team Effectiveness',
    icon: <UsersIcon className="h-8 w-8 text-[#0099A1]" data-id="element-1562" />,
    description: 'Build high-performing teams through evidence-based frameworks and experiential learning.',
    experts: ['Google re:Work Trainers', 'Gallup Certified Coaches', 'Kellogg School']
  }];
  const services = [{
    title: 'Travel Logistics',
    items: ['Room blocks', 'Flight coordination', 'Ground transportation', 'VIP arrivals & departures']
  }, {
    title: 'Event Curation & Design',
    items: ['Custom agendas', 'Brand storytelling', 'Expert trainers', 'Theme development']
  }, {
    title: 'Production & Logistics',
    items: ['Venue consulting', 'Vendor management', 'On-site team', 'Risk management']
  }, {
    title: 'Event Tech',
    items: ['Custom apps', 'AV solutions', 'QR experiences', 'Virtual integrations']
  }, {
    title: 'Wellness & Community',
    items: ['Fitness activities', 'CSR programs', 'Beach clean-ups', 'Reef restoration']
  }];
  return <div className="pt-20 bg-[#F4E8D0]/30" data-id="element-1563">
      {/* Hero */}
      <section className="bg-[#0099A1] py-20" data-id="element-1564">
        <div className="container mx-auto px-4 md:px-6" data-id="element-1565">
          <div className="max-w-3xl" data-id="element-1566">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4" data-id="element-1567">
              Solutions
            </h1>
            <p className="text-xl text-white/90" data-id="element-1568">
              End-to-end event management tailored to your organization's unique
              goals and culture.
            </p>
          </div>
        </div>
      </section>

      {/* Solutions */}
      <section className="py-16 bg-white" data-id="element-1569">
        <div className="container mx-auto px-4 md:px-6" data-id="element-1570">
          <div className="space-y-24" data-id="element-1571">
            {solutions.map((solution, index) => <div key={index} className={`grid md:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'md:grid-flow-dense' : ''}`} data-id="element-1572">
                <div className={index % 2 === 1 ? 'md:col-start-2' : ''} data-id="element-1573">
                  <h2 className="text-3xl font-bold text-[#374047] mb-4" data-id="element-1574">
                    {solution.title}
                  </h2>
                  <p className="text-lg text-[#374047]/80 mb-8" data-id="element-1575">
                    {solution.description}
                  </p>
                  <div className="mb-6" data-id="element-1576">
                    <h3 className="text-xl font-semibold text-[#0099A1] mb-4" data-id="element-1577">
                      Outcomes
                    </h3>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-1578">
                      {solution.outcomes.map((outcome, i) => <li key={i} className="flex items-start" data-id="element-1579">
                          <CheckIcon className="h-5 w-5 text-[#0099A1] flex-shrink-0 mt-0.5" data-id="element-1580" />
                          <span className="ml-3 text-[#374047]" data-id="element-1581">{outcome}</span>
                        </li>)}
                    </ul>
                  </div>
                  <div data-id="element-1582">
                    <h3 className="text-xl font-semibold text-[#0099A1] mb-4" data-id="element-1583">
                      Typical Activities
                    </h3>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-1584">
                      {solution.activities.map((activity, i) => <li key={i} className="flex items-start" data-id="element-1585">
                          <CheckIcon className="h-5 w-5 text-[#0099A1] flex-shrink-0 mt-0.5" data-id="element-1586" />
                          <span className="ml-3 text-[#374047]" data-id="element-1587">
                            {activity}
                          </span>
                        </li>)}
                    </ul>
                  </div>
                  <div className="mt-8" data-id="element-1588">
                    <Button variant="outline" to="/contact" data-id="element-1589">
                      Discuss Your {solution.title}
                    </Button>
                  </div>
                </div>
                <div className={index % 2 === 1 ? 'md:col-start-1' : ''} data-id="element-1590">
                  <div className="relative h-[400px] rounded-xl overflow-hidden shadow-lg" data-id="element-1591">
                    <img src={solution.image} alt={solution.title} className="w-full h-full object-cover" data-id="element-1592" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" data-id="element-1593"></div>
                  </div>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* Expert Trainers Section - NEW */}
      <section className="py-16 bg-[#F4E8D0]/30" data-id="element-1594">
        <div className="container mx-auto px-4 md:px-6" data-id="element-1595">
          <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-1596">
            <h2 className="text-3xl md:text-4xl font-bold text-[#374047] mb-4" data-id="element-1597">
              World-Class Expert Network
            </h2>
            <p className="text-lg text-[#374047]/80" data-id="element-1598">
              Elevate your corporate retreat with specialized training from top
              business school professors and industry experts.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-12 items-center mb-16" data-id="element-1599">
            <div data-id="element-1600">
              <h3 className="text-2xl font-bold text-[#374047] mb-4" data-id="element-1601">
                From Boardroom to Classroom to Beach
              </h3>
              <p className="text-lg text-[#374047]/80 mb-6" data-id="element-1602">
                Our exclusive network of professors from the world's top
                business schools and corporate trainers brings unparalleled
                expertise to your retreat experience.
              </p>
              <p className="text-lg text-[#374047]/80 mb-6" data-id="element-1603">
                We carefully match expert facilitators to your specific
                organizational goals, creating custom learning experiences that
                drive real business outcomes.
              </p>
              <div className="space-y-4" data-id="element-1604">
                <div className="flex items-start" data-id="element-1605">
                  <CheckIcon className="h-5 w-5 text-[#0099A1] flex-shrink-0 mt-0.5" data-id="element-1606" />
                  <span className="ml-3 text-[#374047]" data-id="element-1607">
                    Expert-led workshops integrated into your retreat agenda
                  </span>
                </div>
                <div className="flex items-start" data-id="element-1608">
                  <CheckIcon className="h-5 w-5 text-[#0099A1] flex-shrink-0 mt-0.5" data-id="element-1609" />
                  <span className="ml-3 text-[#374047]" data-id="element-1610">
                    Immersive learning experiences in inspiring settings
                  </span>
                </div>
                <div className="flex items-start" data-id="element-1611">
                  <CheckIcon className="h-5 w-5 text-[#0099A1] flex-shrink-0 mt-0.5" data-id="element-1612" />
                  <span className="ml-3 text-[#374047]" data-id="element-1613">
                    Custom content development aligned with your objectives
                  </span>
                </div>
                <div className="flex items-start" data-id="element-1614">
                  <CheckIcon className="h-5 w-5 text-[#0099A1] flex-shrink-0 mt-0.5" data-id="element-1615" />
                  <span className="ml-3 text-[#374047]" data-id="element-1616">
                    Follow-up implementation support for lasting impact
                  </span>
                </div>
              </div>
            </div>
            <div data-id="element-1617">
              <img src="https://images.unsplash.com/photo-1524178232363-1fb2b075b655?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80" alt="Expert-led workshop" className="rounded-xl shadow-lg" data-id="element-1618" />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-1619">
            {expertCategories.map((category, index) => <Card key={index} className="h-full" data-id="element-1620">
                <CardBody className="p-6" data-id="element-1621">
                  <div className="flex flex-col items-center text-center mb-6" data-id="element-1622">
                    <div className="mb-4 mt-2" data-id="element-1623">{category.icon}</div>
                    <h3 className="text-xl font-bold text-[#374047]" data-id="element-1624">
                      {category.title}
                    </h3>
                  </div>
                  <p className="text-[#374047]/80 mb-6" data-id="element-1625">
                    {category.description}
                  </p>
                  <div className="mt-auto" data-id="element-1626">
                    <h4 className="text-sm font-semibold text-[#0099A1] mb-2" data-id="element-1627">
                      Featured Experts From:
                    </h4>
                    <ul className="space-y-1" data-id="element-1628">
                      {category.experts.map((expert, i) => <li key={i} className="text-sm text-[#374047]" data-id="element-1629">
                          {expert}
                        </li>)}
                    </ul>
                  </div>
                </CardBody>
              </Card>)}
          </div>
          <div className="mt-12 text-center" data-id="element-1630">
            <Button variant="primary" to="/contact" data-id="element-1631">
              <BookOpenIcon className="h-5 w-5 mr-2" data-id="element-1632" />
              Explore Expert-Led Programs
            </Button>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-16 bg-[#F4E8D0]/50" data-id="element-1633">
        <div className="container mx-auto px-4 md:px-6" data-id="element-1634">
          <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-1635">
            <h2 className="text-3xl md:text-4xl font-bold text-[#374047] mb-4" data-id="element-1636">
              Included Services
            </h2>
            <p className="text-lg text-[#374047]/80" data-id="element-1637">
              Every DestaMeet experience includes comprehensive support from our
              expert team.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-1638">
            {services.map((service, index) => <Card key={index} className="h-full" data-id="element-1639">
                <CardBody data-id="element-1640">
                  <h3 className="text-xl font-bold text-[#0099A1] mb-4" data-id="element-1641">
                    {service.title}
                  </h3>
                  <ul className="space-y-3" data-id="element-1642">
                    {service.items.map((item, i) => <li key={i} className="flex items-start" data-id="element-1643">
                        <CheckIcon className="h-5 w-5 text-[#0099A1] flex-shrink-0 mt-0.5" data-id="element-1644" />
                        <span className="ml-3 text-[#374047]" data-id="element-1645">{item}</span>
                      </li>)}
                  </ul>
                </CardBody>
              </Card>)}
          </div>
        </div>
      </section>

      <CtaSection data-id="element-1646" />
    </div>;
}